import React from 'react';
import useThemeStrings from '../hooks/useThemeStrings';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import SyncModal from '../components/SyncModal';
import { localeLabel } from '../utils/localeFlag';

// D-24: prefix dropdown options (10 namespaces + "all").
const KEY_PREFIXES = [
  { value: '', label: 'All prefixes' },
  { value: 'section.', label: 'section.*' },
  { value: 'products.', label: 'products.*' },
  { value: 'customer.', label: 'customer.*' },
  { value: 'cart.', label: 'cart.*' },
  { value: 'general.', label: 'general.*' },
  { value: 'collection.', label: 'collection.*' },
  { value: 'contact.', label: 'contact.*' },
  { value: 'blogs.', label: 'blogs.*' },
  { value: 'customer_accounts.', label: 'customer_accounts.*' },
  { value: 'shopify.', label: 'shopify.*' },
];

// D-24: policy filter buttons. The 'auto-skipped' literal must hit
// `auto_skip_reason IS NOT NULL` in the backend (see Plan 02 SUMMARY).
const POLICY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'translate', label: 'Translate' },
  { id: 'skip', label: 'Skip (user)' },
  { id: 'auto-skipped', label: 'Auto-skipped' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All strings' },
  { id: 'missing', label: 'Missing translations only' },
];

// D-23 column 2: short labels for resource_type so the table doesn't blow out.
const RESOURCE_TYPE_LABELS = {
  ONLINE_STORE_THEME: 'Theme',
  ONLINE_STORE_THEME_SECTION_GROUP: 'Section',
  ONLINE_STORE_THEME_SETTINGS_DATA_SECTIONS: 'Settings data',
  ONLINE_STORE_THEME_SETTINGS_CATEGORY: 'Settings cat',
};

function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

function truncate(value, n = 80) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

// Compute the dropdown's current value for a row.
// - User override (auto_skip_reason IS NULL && translation_policy IS NOT NULL):
//     value = translation_policy ('translate' | 'skip')
// - Else (auto-derived or unset): value = 'auto'
function dropdownValue(row) {
  if (row.auto_skip_reason === null && row.translation_policy !== null) {
    return row.translation_policy;
  }
  return 'auto';
}

export default function ThemeStrings() {
  const {
    rows,
    total,
    loading,
    error,
    keyPrefix,
    setKeyPrefix,
    policy,
    setPolicy,
    status,
    setStatus,
    q,
    setQ,
    page,
    setPage,
    pageSize,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    updatePolicy,
    fetchData,
  } = useThemeStrings();

  const { locales } = useTargetLocales();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  const handlePolicyChange = async (themeStringId, newValue) => {
    // newValue is "auto" | "translate" | "skip" — map "auto" to null per D-25.
    const apiValue = newValue === 'auto' ? null : newValue;
    try {
      await updatePolicy(themeStringId, apiValue);
    } catch (err) {
      // updatePolicy already rolled back optimistic state; surface the error.
      const msg = err.response?.data?.error || err.message || 'unknown error';
      alert(`Failed to update policy: ${msg}`);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">Theme strings</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {lastSynced ? `Last synced: ${new Date(lastSynced).toLocaleString()}` : 'Never synced'}
        {!loading && ` · ${total.toLocaleString()} keys`}
      </p>

      {/* Filter bar (D-24) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={keyPrefix}
          onChange={(e) => setKeyPrefix(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {KEY_PREFIXES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>

        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={status === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="flex gap-1">
          {POLICY_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={policy === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setPolicy(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <Input
          type="text"
          placeholder="Search key or value..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Key</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Auto-skip</TableHead>
                <TableHead>Policy</TableHead>
                {locales.map((loc) => (
                  <TableHead key={loc}>{localeLabel(loc)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">{row.key}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {RESOURCE_TYPE_LABELS[row.resource_type] || row.resource_type}
                  </TableCell>
                  <TableCell className="text-sm" title={row.raw_value || ''}>
                    {truncate(row.raw_value, 80)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row.auto_skip_reason || ''}
                  </TableCell>
                  <TableCell>
                    <select
                      value={dropdownValue(row)}
                      onChange={(e) => handlePolicyChange(row.id, e.target.value)}
                      className="border border-input bg-background h-8 rounded-md px-2 text-sm"
                    >
                      <option value="auto">Auto</option>
                      <option value="translate">Translate</option>
                      <option value="skip">Skip</option>
                    </select>
                  </TableCell>
                  {locales.map((loc) => (
                    <TableCell key={loc}>
                      <StatusBadge status={row[`${loc}_status`]} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/theme-strings/sync/"
        statusEndpoint="/api/transly/theme-strings/sync-status/"
        title="Syncing Theme Strings"
      />
    </div>
  );
}
